import { useState, useEffect } from "react";
import BigNumber from "bignumber.js";
import { useSettingsRHF } from "../hooks/useSettingsRHF";
import { incomeSchema } from "../types/settingsSchemas";
import type { IncomeSettingsData } from "../types/settings";
import { useSettings } from "../contexts/SettingsContext";
import { calculateSuggestedPensions } from "../services/pensionCalculator";

/**
 * Field classification for Income form (two-tier visibility)
 *
 * Essential: shown during onboarding + Settings
 * Advanced: shown only in Settings (behind ShowMoreToggle)
 */
export const INCOME_ESSENTIAL_FIELDS = [
  "employmentIncome",
  "pensionIncome",
  "investmentIncome",
  "rentalIncome",
  "otherIncome",
] as const;

export const INCOME_ADVANCED_FIELDS = [
  "bonusVariableIncome",
  "salaryGrowthRate",
  "businessIncome",
] as const;

/**
 * Shared logic for Income functionality
 * Handles form state, validation, and data management
 * All income values are monthly
 */
export function useIncomeLogic() {
  const { settings, loading: settingsLoading } = useSettings();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pensionSuggestions, setPensionSuggestions] = useState<{
    cppSuggested: number | null;
    oasSuggested: number | null;
    reason: string;
  } | null>(null);

  // Derive profile data from centralized settings (no extra Firestore call)
  const userAge = settings.profile.currentAge;
  const isRetired = settings.profile.isRetired;
  const yearsInCanadaAfterAge18 = settings.profile.yearsInCanadaAfterAge18;

  const {
    formData,
    handleChange,
    handleBlur: saveField,
    loading,
  } = useSettingsRHF<IncomeSettingsData>("income", incomeSchema);

  // Calculate pension suggestions when profile data or income changes
  useEffect(() => {
    // Calculate annual income from form data
    const annualIncome = new BigNumber(formData.employmentIncome ?? 0)
      .plus(formData.investmentIncome ?? 0)
      .plus(formData.rentalIncome ?? 0)
      .times(12)
      .toNumber();

    const suggestions = calculateSuggestedPensions({
      userAge,
      isRetired,
      yearsInCanada: yearsInCanadaAfterAge18,
      annualIncome,
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPensionSuggestions(suggestions);
  }, [userAge, isRetired, yearsInCanadaAfterAge18, formData]);

  // Handle amount changes
  const handleAmountChange = (name: string, amount: number | null) => {
    handleChange(name, amount);
  };

  // Handle field blur (triggers save)
  const handleBlur = (name: string) => {
    saveField(name, formData[name as keyof IncomeSettingsData]);
  };

  // Calculate total monthly income
  const calculateMonthlyIncome = (): number => {
    const total = new BigNumber(formData.employmentIncome ?? 0)
      .plus(formData.pensionIncome ?? 0)
      .plus(formData.cppIncome ?? 0)
      .plus(formData.oasIncome ?? 0)
      .plus(formData.investmentIncome ?? 0)
      .plus(formData.rentalIncome ?? 0)
      .plus(formData.otherIncome ?? 0)
      .plus(formData.bonusVariableIncome ?? 0)
      .plus(formData.businessIncome ?? 0)
      .toNumber();

    return Math.round(total);
  };

  const totalMonthlyIncome = calculateMonthlyIncome();
  const totalAnnualIncome = new BigNumber(totalMonthlyIncome)
    .times(12)
    .toNumber();

  // Determine which fields to show based on retirement status
  const showPension = isRetired;
  const showCPP = isRetired;
  const showOAS = userAge != null && userAge >= 65;

  const toggleAdvanced = () => setShowAdvanced((prev) => !prev);

  return {
    formData,
    userAge,
    isRetired,
    showPension,
    showCPP,
    showOAS,
    showAdvanced,
    toggleAdvanced,
    handleAmountChange,
    handleBlur,
    loading: loading || settingsLoading,
    totalMonthlyIncome,
    totalAnnualIncome,
    pensionSuggestions,
  };
}
